import * as React from "react";
import { useNotify } from "react-admin";
import {
  Box, Card, CardContent, Typography, Button, Grid, TextField,
  CircularProgress, Alert, Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import StorageIcon from "@mui/icons-material/Storage";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import { httpClient } from "../../api/httpClient";
import { API_BASE_URL } from "../../api/config";

const CacheList = () => {
  const [stats, setStats] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [namespace, setNamespace] = React.useState("");
  const notify = useNotify();

  const refresh = React.useCallback(async () => {
    // Le backend n'expose pas GET /cache : on garde le state vide pour
    // afficher uniquement les boutons d'action.
    setLoading(false);
    setStats([]);
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const clearAll = async () => {
    try {
      await httpClient(`${API_BASE_URL}/cache/evict-all`, { method: "DELETE" });
      notify("Cache global vidé", { type: "success" });
      refresh();
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
    }
  };

  const evict = async () => {
    if (!namespace.trim()) return;
    // Endpoint d'éviction par namespace non exposé sur le backend actuel —
    // on tente l'eviction globale et on signale.
    try {
      await httpClient(`${API_BASE_URL}/cache/evict-all`, { method: "DELETE" });
      notify(`Namespace "${namespace}" : éviction globale appliquée (endpoint dédié non disponible)`, { type: "info" });
      refresh();
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <StorageIcon /> Cache
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Gestion du cache Redis. Les opérations sont irréversibles.
      </Alert>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <TextField
                size="small" label="Namespace"
                value={namespace} onChange={(e) => setNamespace(e.target.value)}
                placeholder="ex: users, bookings"
              />
            </Grid>
            <Grid item>
              <Button variant="outlined" color="warning" onClick={evict}
                startIcon={<DeleteSweepIcon />} disabled={!namespace.trim()}>
                Évincer ce namespace
              </Button>
            </Grid>
            <Grid item sx={{ ml: "auto" }}>
              <Button variant="contained" color="error" onClick={clearAll} startIcon={<DeleteSweepIcon />}>
                Vider tout le cache
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Entrées</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Namespace</TableCell>
                  <TableCell>Clés</TableCell>
                  <TableCell>Taille</TableCell>
                  <TableCell>TTL</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">Aucune donnée</TableCell>
                  </TableRow>
                ) : stats.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell>{s.namespace || s.id}</TableCell>
                    <TableCell>{s.keys ?? "—"}</TableCell>
                    <TableCell>{s.size ?? "—"}</TableCell>
                    <TableCell>{s.ttl ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default { list: CacheList };
